/**
 * Shared utilities for JobFormDialog and its sub-components.
 */

export const AUTOMOTIVE_JOB_TYPES = [
  "car key", "auto key", "automotive", "vehicle", "car lockout",
  "auto lockout", "car unlock", "ignition", "transponder", "fob",
  "remote", "car lock", "auto lock"
];

export function isAutomotiveJob(jobTypes, serviceId) {
  const selectedType = jobTypes.find(jt => jt.service_id === serviceId);
  if (!selectedType) return false;
  const typeName = selectedType.name?.toLowerCase() || "";
  return AUTOMOTIVE_JOB_TYPES.some(autoType => typeName.includes(autoType));
}

export function getInitialFormData(selectedDate, getCurrentTime, getToday) {
  return {
    source_id: "",
    service_id: "",
    service_type: "other",
    assigned_to: "",
    title: "",
    customer_id: "",
    customer_name: "",
    customer_phone: "",
    customer_phone2: "",
    customer_email: "",
    customer_address: "",
    address_city: "",
    address_state: "",
    zip_code: "",
    scheduled_date: selectedDate || (getToday ? getToday() : new Date()),
    scheduled_time: getCurrentTime(),
    schedule_type: "exact",
    scheduled_time_end: "",
    is_recurring: false,
    recurring_interval_value: 1,
    recurring_interval_unit: "months",
    customer_phone_ext: "",
    customer_phone2_ext: "",
    latitude: null,
    longitude: null,
    duration_hours: 1,
    notes: "",
    status: "scheduled",
    total_charged: "",
    source_commission_type: "percentage",
    source_commission_value: "",
    technician_commission_type: "percentage",
    technician_commission_value: "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: "",
    additional_technician_id: "",
    additional_technician_ids: [],
    tech_commission_split_mode: "equal",
    tech_commission_splits_map: {},
    additional_tech_commission_type: "percentage",
    additional_tech_commission_value: "50",
    source_extra_fee_enabled: false,
    source_extra_fee_per_job: 0,
    // Per-job tax kill switch (Tax panel toggle)
    tax_disabled: false,
    // Editable date fields (see JobDatesSection)
    created_at: "",
    completed_at: "",
    // Multi-location: which store location this job belongs to. Empty
    // string = company-wide (single-location accounts) or "Unassigned".
    // The JobFormDialog hydrates this from the account's default location
    // when the picker is shown.
    store_location_id: "",
  };
}

/**
 * Get the best commission rate for a technician considering source/service-specific overrides.
 * Priority: service-specific > source-specific > default
 */
export function getTechCommission(tech, sourceId, serviceId) {
  if (!tech) return { type: "percentage", value: "" };
  if (serviceId && tech.commission_by_service) {
    const serviceRate = tech.commission_by_service[serviceId];
    if (serviceRate !== undefined && serviceRate !== null && serviceRate !== "") {
      return { type: "percentage", value: parseFloat(serviceRate) };
    }
  }
  if (sourceId && tech.commission_by_source) {
    const sourceRate = tech.commission_by_source[sourceId];
    if (sourceRate !== undefined && sourceRate !== null && sourceRate !== "") {
      return { type: "percentage", value: parseFloat(sourceRate) };
    }
  }
  if (tech.commission_rate > 0) {
    return { type: tech.commission_type || "percentage", value: tech.commission_rate };
  }
  return { type: "percentage", value: "" };
}

/**
 * Apply an update to a single payment row. Used by both Job and Store-Job
 * dialog completion sections so the "wrong-then-correct payment type"
 * regression doesn't return.
 *
 * Behaviour: when `field === 'payment_type_id'`, the row's `collected_by`
 * is ALWAYS reset to the new type's `default_collected_by`. The user can
 * still toggle Co./Tech afterwards via the in-row button.
 */
export function applyPaymentRowUpdate(row, field, value, paymentTypes) {
  const next = { ...row, [field]: field === "amount" ? parseFloat(value) || 0 : value };
  if (field === "payment_type_id") {
    const pt = (paymentTypes || []).find(p => p.payment_id === value);
    if (pt) {
      next.collected_by = pt.default_collected_by || "company";
    }
  }
  return next;
}


/**
 * Calculate all financial totals for the job form.
 */
export function calculateTotals(formData, parts, payments, teamMembers, paymentTypes, taxRate = 0) {
  const totalCharged = parseFloat(formData.total_charged) || 0;
  const partsCost = parts.reduce((sum, p) => sum + (parseFloat(p.cost) || 0), 0);
  const companyPartsCost = parts.filter(p => p.provider === "company").reduce((sum, p) => sum + (parseFloat(p.cost) || 0), 0);
  const techPartsCost = parts.filter(p => p.provider === "technician").reduce((sum, p) => sum + (parseFloat(p.cost) || 0), 0);
  const paymentsTotal = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

  const selectedTech = formData.assigned_to ? teamMembers.find(m => m.member_id === formData.assigned_to) : null;
  const hasTech = !!selectedTech;
  const shouldChargeProcessingFees = hasTech && selectedTech.charge_processing_fees !== false;

  const actualProcessingFees = payments.reduce((sum, p) => {
    const paymentType = paymentTypes.find(pt => pt.payment_id === p.payment_type_id);
    if (!paymentType || !paymentType.processing_fee) return sum;
    return sum + ((parseFloat(p.amount) || 0) * (paymentType.processing_fee / 100));
  }, 0);

  const processingFees = shouldChargeProcessingFees ? actualProcessingFees : 0;
  const commissionRevenue = Math.max(0, totalCharged - techPartsCost);

  const sourceCommissionValue = parseFloat(formData.source_commission_value) || 0;
  const partsDeduction = formData.source_parts_deduction || "from_total";
  const commissionBase = partsDeduction === "from_total" ? Math.max(0, commissionRevenue - companyPartsCost) : commissionRevenue;
  let sourceCommissionAmount = formData.source_commission_type === "fixed"
    ? sourceCommissionValue
    : (commissionBase * sourceCommissionValue) / 100;

  const sourceExtraFee = (formData.source_extra_fee_enabled && formData.source_extra_fee_per_job > 0)
    ? parseFloat(formData.source_extra_fee_per_job) || 0
    : 0;
  sourceCommissionAmount += sourceExtraFee;

  const techCommissionValue = parseFloat(formData.technician_commission_value) || 0;
  const techPartsDeduction = selectedTech?.parts_deduction || "none";
  const techCommissionBase = techPartsDeduction === "from_total" ? Math.max(0, commissionRevenue - companyPartsCost) : commissionRevenue;
  const techCommissionAmount = formData.technician_commission_type === "fixed"
    ? techCommissionValue
    : (techCommissionBase * techCommissionValue) / 100;

  const netTechCommission = Math.max(0, techCommissionAmount - processingFees);
  const totalCommission = sourceCommissionAmount + techCommissionAmount;

  let additionalTechCommission = 0;
  const additionalIds = (formData.additional_technician_ids || []).filter(t => t && t.trim());
  if (additionalIds.length > 0) {
    const totalTechs = additionalIds.length + 1;
    const eq = 100 / totalTechs;
    if (formData.tech_commission_split_mode === "custom" && formData.tech_commission_split_unit === "amount") {
      // Fixed $ per additional tech — primary keeps the remainder.
      const amap = formData.tech_commission_splits_amount_map || {};
      const othersAmt = additionalIds.reduce((s, id) => s + (parseFloat(amap[id]) || 0), 0);
      additionalTechCommission = Math.min(othersAmt, techCommissionAmount);
    } else if (formData.tech_commission_split_mode === "custom") {
      const map = formData.tech_commission_splits_map || {};
      const othersPct = additionalIds.reduce((s, id) => {
        const v = parseFloat(map[id]);
        return s + (isNaN(v) ? eq : v);
      }, 0);
      additionalTechCommission = (techCommissionAmount * Math.min(Math.max(othersPct, 0), 100)) / 100;
    } else {
      // Equal split of the total tech commission across all techs.
      additionalTechCommission = (techCommissionAmount * additionalIds.length) / totalTechs;
    }
  } else if (formData.additional_technician_id && formData.additional_technician_id.trim() && formData.additional_tech_commission_value) {
    const addValue = parseFloat(formData.additional_tech_commission_value) || 0;
    if (formData.additional_tech_commission_type === "fixed") {
      additionalTechCommission = Math.min(addValue, techCommissionAmount);
    } else {
      additionalTechCommission = (techCommissionAmount * addValue) / 100;
    }
  }
  const primaryTechNet = techCommissionAmount - additionalTechCommission;
  const primaryTechPayout = Math.max(0, primaryTechNet - processingFees) + techPartsCost;
  const additionalTechPayout = additionalTechCommission;

  const profit = shouldChargeProcessingFees
    ? totalCharged - companyPartsCost - techPartsCost - totalCommission + processingFees
    : totalCharged - companyPartsCost - techPartsCost - totalCommission;

  // Tax is ADDED ON TOP of Total Charged (the pre-tax subtotal). Commissions
  // and profit above are computed on the pre-tax amount only — mirrors
  // backend core/tax.py.
  const appliedTaxRate = parseFloat(taxRate) || 0;
  const taxAmount = Math.round(totalCharged * appliedTaxRate) / 100;
  const customerTotal = Math.round((totalCharged + taxAmount) * 100) / 100;

  return {
    totalCharged, partsCost, companyPartsCost, techPartsCost, paymentsTotal,
    processingFees, sourceCommissionAmount, sourceExtraFee, techCommissionAmount, netTechCommission,
    totalCommission, profit, additionalTechCommission, primaryTechNet, primaryTechPayout, additionalTechPayout,
    taxRate: appliedTaxRate, taxAmount, customerTotal
  };
}


/**
 * Build the tech_commission_split payload fields from form state.
 * Returns { mode, unit, splits } — splits only for a valid custom setup.
 * $ mode: additional techs carry fixed amounts; the primary's entry has
 * amount=null (they receive the remainder of the total tech commission).
 */
export const buildTechSplits = (primaryId, additionalIds, mode, percentsMap, unit = "percent", amountsMap = {}) => {
  const ids = [primaryId, ...(additionalIds || [])].filter(t => t && t.trim());
  if (ids.length < 2) return { mode: null, unit: null, splits: null };
  if (mode !== "custom") return { mode: "equal", unit: "percent", splits: null };
  if (unit === "amount") {
    const splits = ids.map(id => id === primaryId
      ? { technician_id: id, amount: null }
      : { technician_id: id, amount: Math.round((parseFloat((amountsMap || {})[id]) || 0) * 100) / 100 });
    return { mode: "custom", unit: "amount", splits };
  }
  const eq = 100 / ids.length;
  const splits = ids.map(id => {
    const v = parseFloat((percentsMap || {})[id]);
    return { technician_id: id, percent: Math.round((isNaN(v) ? eq : v) * 100) / 100 };
  });
  return { mode: "custom", unit: "percent", splits };
};

/**
 * Build a display-friendly full address from structured job fields.
 * Prefers structured fields (address_city, address_state, zip_code).
 * Falls back to customer_address + zip_code for legacy jobs.
 */
export function getDisplayAddress(job) {
  if (!job) return "";
  const street = job.customer_address || "";
  const city = job.address_city || "";
  const state = job.address_state || "";
  const zip = job.zip_code || "";

  // If we have structured fields, build nicely
  if (city || state) {
    const cityState = [city, state].filter(Boolean).join(", ");
    const parts = [street, cityState, zip].filter(Boolean);
    return parts.join(", ");
  }
  // Legacy fallback: street + zip
  return zip ? `${street} ${zip}`.trim() : street;
}

/**
 * Build a Google Maps URL for a job's address
 */
export function getGoogleMapsUrl(job) {
  const addr = getDisplayAddress(job);
  if (!addr) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
}


/**
 * Format a phone number with PIN for call masking display.
 * Example: +15551234567, pin 123 -> "(555) 123-4567,123#"
 */
export function formatMaskedNumberWithPin(phoneNumber, pin) {
  if (!phoneNumber || !pin) return phoneNumber || "";
  const digits = phoneNumber.replace(/\D/g, "");
  const local = digits.startsWith("1") && digits.length === 11 ? digits.slice(1) : digits;
  const formatted = local.length === 10
    ? `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`
    : phoneNumber;
  return `${formatted},${pin}#`;
}
